import {
  useAutoTranslateGameNameMutation,
  useAutoTranslateMutation,
} from "entities/translations/mutations/mutateAutoTranslate";
import { useDynamicTranslationsMutation } from "entities/translations/mutations/mutateDynamicTranslations";
import { isEmpty, get } from "lodash";
import useTranslations from "modules/translations/hooks/useTranslations";
import { TranslationTypes } from "modules/translations/types";
import {
  defineNoEnglishTranslate,
  getTotalUnpublishedChanges,
} from "modules/translations/utils";
import { FC, PropsWithChildren, useState } from "react";
import { useClearRefinements } from "react-instantsearch";
import {
  TranslationError,
  type TranslatedContent,
} from "shared/api/openai/types";
import { LocaleCode } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useDynamicFilter } from "../hooks/useDynamicFilter";
import { useDynamicTranslationsData } from "../hooks/useDynamicTranslationsData";
import { useFilterOptions } from "../hooks/useFilterOptions";
import { DynamicTranslationAlgolia } from "../types";
import { DynamicTranslationPageType, TranslationsDataResponse } from "./types";

const [Provider, useDynamicTranslationPage] =
  createContext<DynamicTranslationPageType>({
    name: "DynamicTranslationsContext",
    hookName: "useDynamicTranslationPage",
    providerName: "DynamicTranslationsProvider",
  });

const DynamicTranslationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { contentOptions, contentItemOptions } = useFilterOptions();

  const { filteredValues: contentFilterValues, ...contentFilter } =
    useDynamicFilter({ options: contentOptions });
  const { filteredValues: contentItemFilterValues, ...contentItemFilter } =
    useDynamicFilter({ options: contentItemOptions });

  const { tableData, isLoading, searchData } = useDynamicTranslationsData({
    contentFilterValues: contentFilterValues,
    contentItemFilterValues: contentItemFilterValues,
  });

  const [hasShownTranslateMessageError, setShownTranslateMessageError] =
    useState(false);
  const [autoTranslateErrorMessage, setAutoTranslateErrorMessage] =
    useState<string>("");
  const { mutateAsync, isPending, isError } = useAutoTranslateMutation();
  const [targetLanguage, setTargetLanguage] = useState<LocaleCode>();
  const {
    mutateAsync: mutateGameNamesAsync,
    isPending: isPendingAutoTranslateGameNames,
  } = useAutoTranslateGameNameMutation();
  const { mutate: publishUpdatedChanges, isPending: isPendingPublish } =
    useDynamicTranslationsMutation();

  const {
    handleUpdatedTranslations,
    setUpdatedTranslations,
    updatedTranslations,
  } = useTranslations<DynamicTranslationAlgolia>(
    tableData.data,
    TranslationTypes.Dynamic,
  );

  const { refine } = useClearRefinements();

  const handlePublishChanges = async () => {
    if (updatedTranslations) {
      await publishUpdatedChanges(updatedTranslations, {
        onSuccess: () => setUpdatedTranslations(null),
      });
    }
  };

  const onSuccessAutoTranslate = (
    response: TranslationsDataResponse,
    lang: LocaleCode,
  ) => {
    if (!isError && !response.errorMessage && response.data !== null) {
      const translationArray = response.data;
      const translations = Array.isArray(translationArray)
        ? translationArray
        : [translationArray];

      translations.forEach((translation: TranslatedContent) => {
        handleUpdatedTranslations({
          translationId: translation.id,
          value: translation[lang],
          locale: lang,
        });
      });
    } else {
      setShownTranslateMessageError(true);
      setAutoTranslateErrorMessage(
        (response as { errorMessage: string }).errorMessage,
      );
    }
  };

  const handleAutoTranslate = async (lang: LocaleCode) => {
    const gameNamesRequestData: { id: string; content: string; en: null }[] =
      [];

    const requestData = tableData.data
      .filter(
        (content) =>
          get(content, "translations.en") &&
          !get(content, `translations.${lang}`) &&
          !updatedTranslations?.[content.id],
      )
      .filter((content) => {
        if (content.contentItem === "Game name") {
          gameNamesRequestData.push({
            id: content.id,
            content: content.contentItem,
            en: get(content, "translations.en") || null,
          });
          return false;
        }
        return content;
      })
      .map((content) => ({
        id: content.id,
        content: content.contentItem,
        en: get(content, "translations.en") || null,
      }));

    if (!requestData.length && !gameNamesRequestData.length) {
      setShownTranslateMessageError(true);
      setAutoTranslateErrorMessage("There is no data to be autotranslated");
      return;
    }

    try {
      setTargetLanguage(lang);

      if (requestData.length) {
        await mutateAsync(
          { langCode: lang, data: requestData },
          {
            onSuccess: (data) => onSuccessAutoTranslate(data, lang),
            onError: (error: TranslationError) => {
              if (error.errorMessage) {
                setShownTranslateMessageError(true);
                setAutoTranslateErrorMessage(error.errorMessage);
              }
            },
          },
        );
      }

      if (gameNamesRequestData.length) {
        await mutateGameNamesAsync(
          { langCode: lang, data: gameNamesRequestData },
          {
            onSuccess: (data) => onSuccessAutoTranslate(data, lang),
            onError: (error: TranslationError) => {
              if (error.errorMessage) {
                setShownTranslateMessageError(true);
                setAutoTranslateErrorMessage(error.errorMessage);
              }
            },
          },
        );
      }
    } catch (error) {
      setShownTranslateMessageError(true);
      setAutoTranslateErrorMessage(
        "An unexpected error occurred during translation",
      );
    } finally {
      setTargetLanguage(undefined);
    }
  };

  const onClearFilters = () => {
    contentFilter.setFilteredValues([]);
    contentItemFilter.setFilteredValues([]);
    searchData.onChange("");
    refine();
  };

  const hasNoEnglishTranslate = defineNoEnglishTranslate(updatedTranslations);
  const isDisabledPublish =
    isEmpty(updatedTranslations) || Boolean(hasNoEnglishTranslate);

  const totalAmountUnpublishedChanges =
    getTotalUnpublishedChanges(updatedTranslations);

  const value: DynamicTranslationPageType = {
    tableData,
    isLoadingTranslations: isLoading,
    isAutoTranslationLoaded: isPending || isPendingAutoTranslateGameNames,
    hasShownTranslateMessageError,
    autoTranslateErrorMessage,
    updatedTranslations,
    isDisabledPublish,
    isPendingPublish,
    totalAmountUnpublishedChanges,
    handleUpdatedTranslations,
    handlePublishChanges,
    setShownTranslateMessageError,
    handleAutoTranslate,
    searchData,
    contentFilterData: contentFilter.filterData,
    contentItemFilterData: contentItemFilter.filterData,
    onClearFilters,
    targetLanguage,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { DynamicTranslationsProvider, useDynamicTranslationPage };
